import React, { useEffect, useState } from "react";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Utensils, Crown } from "lucide-react";

/**
 * Zoom-aware restaurant markers with lightweight clustering at low zoom.
 *
 * Why custom grouping instead of @googlemaps/markerclusterer:
 *  - The official clusterer creates raw `AdvancedMarkerElement` instances and
 *    manages their `.map` attachment imperatively. With @vis.gl/react-google-maps
 *    + Map IDs, those instances sometimes end up outside the map's overlay pane
 *    (rendered at the bottom of the document) — markers exist in the DOM but
 *    are invisible on the map. Custom grouping side-steps the problem entirely.
 *
 * Algorithm: at zoom < CLUSTER_BELOW we bucket restaurants by a coarse lat/lng
 * grid. Singletons render as normal pins. Multi-restaurant buckets render as a
 * single "count pin" centered on the bucket. Featured spots NEVER cluster —
 * they paid to be seen.
 */
const CLUSTER_BELOW = 13; // start clustering at this zoom and below
const GRID_AT = {
  // approximate cell size in degrees per zoom level
  12: 0.012,
  11: 0.025,
  10: 0.05,
  9: 0.1,
  8: 0.2,
  default: 0.4,
};

export default function ClusteredMarkers({ restaurants, activeId, onMarkerClick }) {
  const map = useMap();
  const [zoom, setZoom] = useState(13);

  useEffect(() => {
    if (!map) return;
    const update = () => {
      const z = map.getZoom();
      if (typeof z === "number") setZoom(z);
    };
    update();
    const listener = map.addListener("zoom_changed", update);
    return () => listener?.remove?.();
  }, [map]);

  // Compute the cluster buckets only when zoom is low enough that overlap
  // becomes a problem; above CLUSTER_BELOW we just render every pin.
  const { singles, clusters } = group(restaurants, zoom);

  return (
    <>
      {singles.map((r) => (
        <RestaurantMarker
          key={r.place_id}
          restaurant={r}
          active={activeId === r.place_id}
          onClick={onMarkerClick}
        />
      ))}
      {clusters.map((c) => (
        <ClusterMarker
          key={c.key}
          cluster={c}
          onClick={() => {
            if (!map) return;
            // Zoom in to expand the cluster, centered on its centroid
            map.panTo({ lat: c.lat, lng: c.lng });
            map.setZoom(Math.min(20, (map.getZoom() || 12) + 2));
          }}
        />
      ))}
    </>
  );
}

function group(restaurants, zoom) {
  if (zoom > CLUSTER_BELOW) {
    return { singles: restaurants, clusters: [] };
  }
  const grid = GRID_AT[zoom] || GRID_AT.default;
  const buckets = new Map();
  const singles = [];
  for (const r of restaurants) {
    if (r.featured) {
      singles.push(r);
      continue;
    }
    const gx = Math.floor(r.lat / grid);
    const gy = Math.floor(r.lng / grid);
    const key = `${gx}:${gy}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(r);
  }
  const clusters = [];
  for (const [key, group] of buckets) {
    if (group.length === 1) {
      singles.push(group[0]);
    } else {
      // Centroid of bucket
      const lat = group.reduce((s, x) => s + x.lat, 0) / group.length;
      const lng = group.reduce((s, x) => s + x.lng, 0) / group.length;
      clusters.push({ key, lat, lng, count: group.length });
    }
  }
  return { singles, clusters };
}

function RestaurantMarker({ restaurant, active, onClick }) {
  const featured = restaurant.featured;
  const tier = restaurant.featured_tier;
  const featuredColor =
    tier === "premium" ? "#E1A624" : tier === "pro" ? "#E1A624" : "#D95D39";
  return (
    <AdvancedMarker
      position={{ lat: restaurant.lat, lng: restaurant.lng }}
      onClick={() => onClick?.(restaurant.place_id)}
      zIndex={featured ? 500 : active ? 999 : 1}
    >
      <div
        data-testid={`map-marker-${restaurant.place_id}`}
        className={`marker-pop relative flex items-center justify-center rounded-full shadow-lg transition-all duration-200 cursor-pointer border-2 border-white
          ${
            featured
              ? "w-12 h-12 hover:scale-110"
              : active
              ? "w-12 h-12 bg-[#D95D39] ring-4 ring-[#D95D39]/25"
              : "w-9 h-9 bg-[#D95D39] hover:scale-110 hover:bg-[#C54E2C]"
          }`}
        style={
          featured
            ? {
                backgroundColor: featuredColor,
                boxShadow: `0 0 0 4px ${featuredColor}33, 0 6px 14px rgba(0,0,0,0.18)`,
              }
            : undefined
        }
      >
        {featured ? (
          <Crown className="text-white w-5 h-5" strokeWidth={2.4} />
        ) : (
          <Utensils
            className={`text-white ${active ? "w-5 h-5" : "w-4 h-4"}`}
            strokeWidth={2.4}
          />
        )}
        {(active || featured) && (
          <span
            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 border-r-2 border-b-2 border-white"
            style={{ backgroundColor: featured ? featuredColor : "#D95D39" }}
          />
        )}
      </div>
    </AdvancedMarker>
  );
}

function ClusterMarker({ cluster, onClick }) {
  const size =
    cluster.count < 10 ? 40 : cluster.count < 100 ? 48 : 56;
  return (
    <AdvancedMarker
      position={{ lat: cluster.lat, lng: cluster.lng }}
      onClick={onClick}
      zIndex={400}
    >
      <div
        data-testid="map-cluster"
        className="relative flex items-center justify-center rounded-full text-white font-bold cursor-pointer transition-transform duration-150 hover:scale-110"
        style={{
          width: size,
          height: size,
          background: "#2A3B2C",
          border: "3px solid #FDFBF7",
          boxShadow: "0 0 0 4px rgba(42,59,44,.18), 0 6px 16px rgba(0,0,0,.2)",
          fontSize: cluster.count < 100 ? 14 : 12,
          letterSpacing: ".5px",
        }}
      >
        {cluster.count}
      </div>
    </AdvancedMarker>
  );
}
